/**
 * Turns `ALL_INQUIRY_PATIENTS_QUERY` results into queue rows.
 *
 * Rooted on the Patient. The query's `_lastUpdated ge` window is a superset —
 * it also returns older Inquiry leads someone touched this week — so the real
 * membership test is the sign-up date, and that comes from hydration:
 *
 *   0. the Patient's own `PATIENT_CREATED_AT_EXTENSION` (BAR-2787) — exact,
 *      stamped once at creation, and free: it is on the query;
 *   1. the eligibility submission's `authored` — the moment the lead signed up.
 *      On production (2026-09-28) it covered 1,108 of 1,155 cohort patients and
 *      sat within minutes of the Patient's creation (median 1, max 168);
 *   2. failing that, the earliest `EpisodeOfCare.period.start`, a partner-track
 *      enrolment that exists only for B2B patients (133 of the 1,155);
 *   3. failing that, the Patient's creation version (`createdAtByPatient`, the
 *      oldest entry in its history). Exact, but ~10 points a patient, so
 *      hydration reads it only for the patients neither of the above dates.
 *
 * Membership itself — still at Inquiry, signed up inside the window — lives in
 * `@bariendo/lib/pac-inquiry-list`.
 */

import { getReferenceString } from '@medplum/core';
import { HumanName, Practitioner } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { ActiveLeadState } from '@bariendo/lib/active-lead';
import { getCareTeamMembersInPool } from '@bariendo/lib/care-team-roles';
import { countContactAttempts } from '@bariendo/lib/communication';
import { PATIENT_FILTER_TAG_SYSTEMS, Track } from '@bariendo/lib/constants';
import { readEligibilityIntakeSelections, readEligibilityPayment } from '@bariendo/lib/eligibility-intake';
import { getTrackOrSelfPay } from '@bariendo/lib/episode-of-care';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { readPatientCreatedAt } from '@bariendo/lib/patient-created-at';
import { getPaymentMethod, parsePatientAccessCallAnswers } from '@bariendo/lib/pac-call-notes';
import {
  formatPaymentMethodCell,
  isEmployerBenefitPayment,
  isInquiryLead,
  isInsurancePayment,
  readEmployer,
} from '@bariendo/lib/pac-inquiry-list';
import { hasOverdueTask } from '@bariendo/lib/pac-post-consultation-list';
import {
  activeLeadFor,
  hydratedAppointments,
  LastCommunicationCell,
  NextTaskCell,
  PreConsultationPatientResult,
  StickyNoteCell,
  toLastCommunicationCell,
  toNextTaskCell,
  toStickyNoteCell,
} from './listRowShared';
import { hydratedPractitionerNames, PacQueueHydration } from '../hooks/usePacQueueHydration';

/** The patient as the query selects it; the eligibility form arrives through hydration. */
export type InquiryPatientResult = PreConsultationPatientResult;

export interface InquiryLeadRow {
  id: string;
  patient: InquiryPatientResult;
  /** When the Patient was created — the sign-up date. */
  signupAt: string;
  location?: string;
  /** The offering they are interested in, as stored: `ESG`, `IGB`, `TORe`. */
  procedure?: string;
  track?: Track;
  /** Ready to render: "Employer benefit · Carrum Health", "Insurance · Aetna", or the bare answer. */
  paymentMethod?: string;
  /** The lead expects an employer to pay — drives the chip, by the cell's own rule. */
  employerBenefit: boolean;
  /** The lead's own insurance pays — drives the Insurance chip. Never true alongside `employerBenefit`. */
  insurance: boolean;
  lastCommunication?: LastCommunicationCell;
  nextTask?: NextTaskCell;
  hasOverdueTask: boolean;
  employer?: string;
  /** Every Patient Navigator on the CareTeam. */
  patientNavigators: HumanName[];
  patientNavigatorPractitioners: Practitioner[];
  stickyNote?: StickyNoteCell;
  activeLead: ActiveLeadState;
}

/** The Intake hub's rules in place of the legacy tab's seven-day window (BAR-2808). */
export interface InquiryHubOptions {
  /** The Signed up preset's start (`YYYY-MM-DD`); absent for All time. */
  signedUpFrom?: string;
}

export function buildInquiryRows(
  patients: InquiryPatientResult[],
  hydration: PacQueueHydration,
  now: Date,
  hub?: InquiryHubOptions
): InquiryLeadRow[] {
  const rows: InquiryLeadRow[] = [];

  for (const patient of patients) {
    const patientRef = patient.id ? getReferenceString(patient) : undefined;

    if (!patient.id || !patientRef) {
      continue;
    }

    const episode = hydration.episodeByPatient.get(patientRef);
    const eligibility = hydration.eligibilityByPatient.get(patientRef);
    const signupAt =
      readPatientCreatedAt(patient) ??
      eligibility?.authored ??
      episode?.period?.start ??
      hydration.createdAtByPatient.get(patientRef);

    if (!signupAt) {
      continue;
    }

    if (hub) {
      // Membership is the sync's queue tag (BAR-2877), the same one the query filters on;
      // this catches rows that arrive by subscription. The date trims the query's UTC-day edges.
      const inQueue = patient.meta?.tag?.some(
        (tag) => tag.system === PATIENT_FILTER_TAG_SYSTEMS.queue && tag.code === 'inquiry'
      );
      const signedUpInRange = !hub.signedUpFrom || !dayjs(signupAt).isBefore(hub.signedUpFrom, 'day');
      if (!inQueue || !signedUpInRange) {
        continue;
      }
    } else if (!isInquiryLead({ patient, signupAt }, now)) {
      continue;
    }

    const latestNote = hydration.callNotesByPatient.get(patientRef)?.[0];
    const answers = latestNote ? parsePatientAccessCallAnswers(latestNote) : undefined;
    const track = getTrackOrSelfPay(episode);
    const openTasks = hydration.tasksByPatient.get(patientRef);
    // A rep's confirmed answer on the intake card outranks what the lead
    // self-reported on the form; the form outranks the track default.
    const selfReported = readEligibilityPayment(eligibility);
    const payment = {
      paymentMethod: getPaymentMethod(answers) ?? selfReported.paymentMethod,
      track,
      employer: readEmployer(patient) ?? selfReported.employer,
      payer: hydration.insurancePayerByPatient.get(patientRef),
    };
    const lastCommunicationResult = hydration.lastCommunicationByPatient.get(patientRef);
    const lastCommunication = toLastCommunicationCell(lastCommunicationResult);
    const navigatorRefs = getCareTeamMembersInPool(
      hydration.careTeamByPatient.get(patientRef),
      hydration.patientNavigatorPool
    );

    rows.push({
      id: patient.id,
      patient,
      signupAt,
      location: readMarketLocation(patient, answers),
      procedure: readInterestedService(patient, answers, readEligibilityIntakeSelections(eligibility).procedure),
      track,
      paymentMethod: formatPaymentMethodCell(payment),
      employerBenefit: isEmployerBenefitPayment(payment),
      insurance: isInsurancePayment(payment),
      lastCommunication: lastCommunication && {
        ...lastCommunication,
        // Counted since signup on Inquiry.
        attempts: countContactAttempts(hydration.communicationsByPatient.get(patientRef), signupAt),
      },
      nextTask: toNextTaskCell(openTasks, hydration.practitionerByRef),
      hasOverdueTask: hasOverdueTask(openTasks, now),
      employer: payment.employer,
      patientNavigators: hydratedPractitionerNames(navigatorRefs, hydration.practitionerByRef),
      patientNavigatorPractitioners: navigatorRefs
        .map((reference) => hydration.practitionerByRef.get(reference))
        .filter((practitioner): practitioner is Practitioner => practitioner !== undefined),
      stickyNote: toStickyNoteCell(hydration.stickyNoteByPatient.get(patientRef), hydration.practitionerByRef),
      activeLead: activeLeadFor({
        lastCommunicationSent: lastCommunicationResult?.sent,
        leadFlags: hydration.flagsByPatient.get(patientRef),
        signupAt,
        appointments: hydratedAppointments(hydration, patientRef),
        now,
      }),
    });
  }

  return rows;
}
